<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  },
  availableSeasons: {
    type: Array,
    default: () => []
  },
  selectedSeasons: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'update-selected-seasons'])

// Search functionality
const seasonSearch = ref('')

// Filtered seasons based on search
const filteredSeasons = computed(() => {
  if (!seasonSearch.value.trim()) {
    return props.availableSeasons
  }
  
  const searchTerm = seasonSearch.value.toLowerCase().trim()
  return props.availableSeasons.filter(season => 
    season.toLowerCase().includes(searchTerm)
  )
})

// Safe selected seasons
const safeSelectedSeasons = computed(() => Array.isArray(props.selectedSeasons) ? props.selectedSeasons : [])

function toggleSeason(season) {
  let newSeasons = [...safeSelectedSeasons.value]
  const idx = newSeasons.indexOf(season)
  if (idx === -1) {
    newSeasons.push(season)
  } else {
    newSeasons.splice(idx, 1)
  }
  emit('update-selected-seasons', newSeasons)
}

function toggleAllSeasons() {
  if (safeSelectedSeasons.value.length === props.availableSeasons.length) {
    // If all are selected, deselect all
    emit('update-selected-seasons', [])
  } else {
    // If not all are selected, select all
    emit('update-selected-seasons', [...props.availableSeasons])
  }
}

// Check if all seasons are selected
const allSeasonsSelected = computed(() => {
  return safeSelectedSeasons.value.length === props.availableSeasons.length && props.availableSeasons.length > 0
})

// Close popup when clicking outside
const popupRef = ref(null)

const handleClickOutside = (event) => {
  if (popupRef.value && !popupRef.value.contains(event.target)) {
    emit('close')
  }
}

const handleEscape = (event) => {
  if (event.key === 'Escape') {
    emit('close')
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleEscape)
})
</script>

<template>
  <div v-if="isVisible" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
    <div ref="popupRef" class="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200">
        <h3 class="text-xl font-semibold text-gray-900">Select Seasons</h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <i class="fas fa-times text-xl"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="p-6">
        <!-- Toggle All Button -->
        <div class="mb-4">
          <button
            @click="toggleAllSeasons"
            class="w-full px-4 py-3 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-md border border-blue-200 hover:border-blue-300 transition-colors flex items-center justify-center gap-2"
          >
            <i class="fas" :class="allSeasonsSelected ? 'fa-check-square' : 'fa-square'"></i>
            {{ allSeasonsSelected ? 'Deselect All' : 'Select All' }}
          </button>
        </div>
        
        <!-- Search Input -->
        <div class="mb-4">
          <input 
            v-model="seasonSearch"
            type="text"
            placeholder="Search seasons..."
            class="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <!-- Seasons Grid -->
        <div class="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
          <label 
            v-for="season in filteredSeasons" 
            :key="season" 
            class="flex items-center gap-3 cursor-pointer rounded-lg px-4 py-3 border border-gray-200 hover:bg-gray-50 transition-colors"
            :class="safeSelectedSeasons.includes(season) ? 'bg-blue-50 border-blue-300' : ''"
          >
            <input 
              type="checkbox"
              :checked="safeSelectedSeasons.includes(season)"
              @change="() => toggleSeason(season)"
              class="form-checkbox h-5 w-5 text-blue-500 border-blue-400 focus:ring-0" 
            />
            <span :class="safeSelectedSeasons.includes(season) ? 'font-semibold text-blue-700' : 'text-gray-700'">
              {{ season }}
            </span>
          </label>
        </div>
        
        <!-- No results message -->
        <div v-if="filteredSeasons.length === 0 && seasonSearch.trim()" class="text-center text-gray-500 text-sm py-8">
          No seasons found matching "{{ seasonSearch }}"
        </div>
        
        <!-- Selected count -->
        <div class="mt-4 text-sm text-gray-600">
          {{ safeSelectedSeasons.length }} of {{ availableSeasons.length }} seasons selected
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end p-6 border-t border-gray-200">
        <button @click="$emit('close')" class="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">
          Done
        </button>
      </div>
    </div>
  </div>
</template>
